// Upload queue — R7.8/R7.9: a completed step is stored on the device until the server
// acknowledges it, so a WiFi gap during a pallet loses nothing. Uploads are idempotent
// on the server (event ids), so retrying a batch that did arrive is harmless.

import { Api, ApiError, type AutomationEventIn, type StepBatch } from "../api/client.js";

const KEY = "trail.stepQueue";

export interface QueuedStep {
  id: string;
  batch: StepBatch;
  queuedAt: string;
  attempts: number;
  lastError: string | null;
  /** Automation events that belong to the moment the step is stored (pallet closed → label). Fired
   *  by the queue when the step goes through later; the screen fires them itself when it goes
   *  through at once (owner 2026-11-02: a receipt that waited out a server restart got no label). */
  after?: AutomationEventIn[];
}

type Listener = (pending: QueuedStep[]) => void;
const listeners = new Set<Listener>();
let flushing = false;

function load(): QueuedStep[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as QueuedStep[]) : [];
  } catch {
    return [];
  }
}

function save(items: QueuedStep[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* quota */
  }
  for (const l of listeners) l(items);
}

export function pending(): QueuedStep[] {
  return load();
}

export function subscribe(l: Listener): () => void {
  listeners.add(l);
  l(load());
  return () => listeners.delete(l);
}

/** Store the step, then try to upload at once. Resolves "stored" when the server has
 *  it, "queued" when it stays on the device for a later retry. */
export async function submitStep(batch: StepBatch, after?: AutomationEventIn[]): Promise<"stored" | "queued"> {
  const item: QueuedStep = { id: batch.events[0]?.batchId ?? String(Date.now()), batch, queuedAt: new Date().toISOString(), attempts: 0, lastError: null };
  save([...load(), item]);
  await flush();
  const queued = load().some((q) => q.id === item.id);
  // Still on the device: the follow-ups ride along, so a later flush fires them (the caller fires them itself when stored now).
  if (queued && after?.length) save(load().map((q) => (q.id === item.id ? { ...q, after } : q)));
  return queued ? "queued" : "stored";
}

/** Upload everything pending, oldest first; stop at the first network failure. A batch
 *  the server rejects as invalid (4xx other than 401/429) is kept and flagged — it needs a
 *  person, not a retry. */
export async function flush(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    let items = load();
    for (const item of [...items]) {
      try {
        await Api.postStep(item.batch);
        items = items.filter((q) => q.id !== item.id);
        save(items);
        for (const e of item.after ?? []) Api.automationEvent(e).catch(() => undefined);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        items = items.map((q) => (q.id === item.id ? { ...q, attempts: q.attempts + 1, lastError: msg } : q));
        save(items);
        if (!(err instanceof ApiError) || err.status === 401 || err.status === 429 || err.status >= 500) break; // offline or server trouble: try the rest later
      }
    }
  } finally {
    flushing = false;
  }
}

export function discard(id: string): void {
  save(load().filter((q) => q.id !== id));
}

let started = false;
/** Retry on reconnect and every half minute while something is pending. */
export function startQueueWorker(): void {
  if (started) return;
  started = true;
  window.addEventListener("online", () => void flush());
  window.setInterval(() => {
    if (load().length > 0 && navigator.onLine) void flush();
  }, 30_000);
}
